import { RefreshCw } from "lucide-react";
import { Link } from "wouter";
import {
  PageHeading, Section, Field, Steps, NextStep, Callout, RoleBadge, pathFor,
  Diagram, MockField, MockButton,
} from "../shared";

export default function FormsUpdateSection() {
  return (
    <div data-testid="guide-forms-update">
      <PageHeading
        icon={RefreshCw}
        title="Add a Progress Update"
        description="Write a short note saying what you've done on a task. Everyone watching the task can see it, and the owner gets an email."
      />

      <div className="flex items-center gap-2 mb-4">
        <RoleBadge role="member" />
        <span className="text-xs text-white/40">Available to the owner, the current holder, sub-task assignees, and any admin.</span>
      </div>

      <Callout variant="info" title="In plain words">
        This is like <span className="text-white">leaving a note on the task</span> so others know how it's going. It doesn't change the status or move the task — it just records progress.
      </Callout>

      <Section title="When do I use this?">
        <ul className="list-disc list-inside text-sm text-white/65 space-y-1.5 ml-2">
          <li>Whenever you make progress and want the owner (and the record) to know.</li>
          <li>When a reviewer has sent the task back and you want to say you've fixed it.</li>
        </ul>
      </Section>

      <Section title="What the form looks like">
        <Diagram
          frameLabel="Updates → Add Update"
          legend={[
            <>Type your <span className="text-white">note</span> — what you've done since last time.</>,
            <>Click <span className="text-white">Add Update</span> to save it.</>,
          ]}
        >
          <MockField label="Note" placeholder="Drafted section 3 of the pack — sent to legal for review." marker={1} />
          <MockButton label="Add Update" marker={2} />
        </Diagram>
      </Section>

      <Section title="Step-by-step">
        <Steps
          steps={[
            <>Open the <span className="text-white">Updates</span> page (best — it shows all your tasks in one place), or open a single task's detail page.</>,
            <>Find the <span className="text-white">Add Update</span> box near the top of the activity timeline, and type your note.</>,
            <>Click <span className="text-white">Add Update</span> to save it.</>,
          ]}
        />
      </Section>

      <Section title="The one box explained">
        <Field name="Note" required="required">
          <p>What you've done since the last update. Be specific and tied to a time.</p>
          <p className="text-white/40">Good: "Drafted section 3 of the pack — sent to legal for review.", "Met with portfolio team; revised assumptions in model v2."</p>
          <p className="text-white/40">Avoid: "Working on it." — that tells the next reader nothing.</p>
        </Field>
      </Section>

      <NextStep>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>The note is added to the task's history with your name and the time.</li>
          <li>It shows in the activity timeline straight away and on the Updates page.</li>
          <li>The task's owner gets an email with your note (unless the owner is the one posting).</li>
          <li>The recorded event type is <span className="text-white">PROGRESS_UPDATE</span>.</li>
        </ul>
      </NextStep>

      <Callout variant="tip">
        If a reviewer has returned the item to In Progress, you'll see an orange banner above the box with their reason — your update should answer that reason.
        See <Link href={pathFor("forms/return")} className="text-cyan-300 hover:underline">Return for Amendments</Link> for the other side of this flow.
      </Callout>
    </div>
  );
}
